// The public Pricing page at /pricing (and /es/pricing): Free vs Pro for visitors, the 14-day
// trial, the money-back guarantee and the founding discount. Members choose a plan on /app/plans.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PublicShell, usePageLanguage, useTitle } from './PublicPage';
import { useI18n } from './i18n';
import { Icon } from './Icons';
import { money } from './Plans';
import { COMING_LATER, FREE_ACCOUNTS, FREE_FEATURES, PRICES, PRICING_FAQ, PRO_FEATURES, REFUND_DAYS, TRIAL_DAYS } from './planData';
import { trackClick } from './analytics';


const fill = (text, vars) => text.replace(/\{(\w+)\}/g, (m, k) => (vars[k] === undefined ? m : vars[k]));

export default function PricingPage({ user, forceLang }) {
  usePageLanguage(forceLang, '/pricing');
  const { t } = useI18n();
  useTitle(t('Pricing: Free and Pro | Market Matrix'));
  const [interval, setInterval_] = useState('year');
  const perMonth = interval === 'year' ? PRICES.year / 12 : PRICES.month;
  const startTo = user ? '/app/plans' : '/signup';
  const subscribed = Boolean(user && user.plan && user.plan.subscribed);
  const startLabel = !user ? t('Start your {n}-day free trial', { n: TRIAL_DAYS }) : subscribed ? t('Manage your plan') : t('Upgrade to Pro');
  const vars = { trial: TRIAL_DAYS, refund: REFUND_DAYS, foundMonth: money(PRICES.month / 2), foundYear: money(PRICES.year / 2) };

  return (
    <PublicShell user={user} className="firm-page pricing-page">
      <header className="page-title firm-hero">
        <p className="eyebrow">{t('Pricing')}</p>
        <h1>{t('Start free. Upgrade when you outgrow it.')}</h1>
        <p className="subtitle">{t('Get every feature free for {n} days, no credit card. After your trial, keep the core journal for free or upgrade to Pro.', { n: TRIAL_DAYS })}</p>
      </header>

      <div className="plans-page">
        <div className="plans-interval" role="group" aria-label={t('Billing')}>
          <button type="button" className={`secondary small${interval === 'month' ? ' active' : ''}`} aria-pressed={interval === 'month'} onClick={() => setInterval_('month')}>{t('Monthly')}</button>
          <button type="button" className={`secondary small${interval === 'year' ? ' active' : ''}`} aria-pressed={interval === 'year'} onClick={() => setInterval_('year')}>
            {t('Yearly')} <span className="plans-save">{t('Save {n}%', { n: Math.round((1 - PRICES.year / (PRICES.month * 12)) * 100) })}</span>
          </button>
        </div>

        <div className="plans-grid">
          <section className="plan-card">
            <h2>{t('Free')}</h2>
            <p className="plan-price"><strong>$0</strong></p>
            <p className="field-hint">{t('The core journal, free for good.')}</p>
            <ul>{FREE_FEATURES.map((f) => <li key={f}>{t(f, { n: FREE_ACCOUNTS })}</li>)}</ul>
            <Link to={user ? '/app' : '/signup'} className="button-secondary plan-cta">{user ? t('Open journal') : t('Start free')}</Link>
          </section>

          <section className="plan-card pro">
            <h2><Icon name="star" /> {t('Pro')}</h2>
            <p className="plan-price"><strong>{money(perMonth)}</strong> <span>{t('a month')}</span></p>
            <p className="field-hint">{interval === 'year' ? t('{price} billed once a year', { price: money(PRICES.year) }) : t('Billed monthly. Cancel any time.')}</p>
            <ul>{PRO_FEATURES.map((f) => <li key={f}>{t(f)}</li>)}</ul>
            {COMING_LATER && <p className="field-hint plan-later">{t(COMING_LATER)}</p>}
            <Link to={startTo} className="cta-button plan-cta" onClick={() => trackClick('pricing-start')}>{startLabel} &rarr;</Link>
            <p className="field-hint plan-note">{t('{n}-day money-back guarantee on your first payment.', { n: REFUND_DAYS })}</p>
          </section>
        </div>

        <p className="pricing-founding">★ {t('Founding members get 50% off any plan for as long as their account stays open: {month} a month or {year} a year for Pro today.', { month: money(PRICES.month / 2), year: money(PRICES.year / 2) })}</p>
        <p className="field-hint pricing-tax">{t('Prices are in US dollars. Sales tax or VAT is added at checkout where it applies.')}</p>

        <section className="firm-section">
          <h2>{t('Questions')}</h2>
          <dl className="firm-faq">
            {PRICING_FAQ.map(([q, a]) => (
              <div key={q}>
                <dt>{t(q)}</dt>
                <dd>{fill(t(a), vars)}{q === 'Is there a refund?' && <> <Link to="/refunds">{t('Read the Refund Policy')} &rarr;</Link></>}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>
    </PublicShell>
  );
}
